import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Bookmark, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDeleteSavedSearch, useRenameSavedSearch } from '@/hooks/use-saved-searches'
import { useMe } from '@/hooks/use-me'
import type { SavedSearch } from '@/lib/saved-searches'

/** One saved search in the sidebar, with a hover menu to rename or delete it.
 *  Org searches are shared, so the delete confirm spells that out. */
export function SavedSearchItem({ search, isActive }: { search: SavedSearch; isActive: boolean }) {
  const me = useMe()
  const rename = useRenameSavedSearch()
  const remove = useDeleteSavedSearch()
  const [renaming, setRenaming] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [draftName, setDraftName] = useState(search.name)

  // You own your user-scoped searches; org searches are shared, so anyone may edit.
  const canEdit = search.scope === 'org' || search.owner === me?.login
  const href = `/q?filter=${encodeURIComponent(search.filter)}&name=${encodeURIComponent(search.name)}`

  function submitRename() {
    const name = draftName.trim()
    if (!name || name === search.name) {
      setRenaming(false)
      return
    }
    rename.mutate(
      { search, name },
      {
        onSuccess: () => setRenaming(false),
        onError: (err) =>
          toast.error('Could not rename', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    )
  }

  function submitDelete() {
    remove.mutate(search, {
      onSuccess: () => {
        toast.success(`Deleted "${search.name}"`)
        setConfirmingDelete(false)
      },
      onError: (err) =>
        toast.error('Could not delete', {
          description: err instanceof Error ? err.message : undefined,
        }),
    })
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={search.name}>
        <Link to={href}>
          <Bookmark className="size-4" />
          <span>{search.name}</span>
        </Link>
      </SidebarMenuButton>

      {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction showOnHover aria-label={`Manage "${search.name}"`}>
              <MoreHorizontal />
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start">
            <DropdownMenuItem
              onSelect={() => {
                setDraftName(search.name)
                setRenaming(true)
              }}
            >
              <Pencil className="size-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={() => setConfirmingDelete(true)}>
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename search</DialogTitle>
          </DialogHeader>
          <Input
            value={draftName}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !rename.isPending && submitRename()}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={!draftName.trim() || rename.isPending}>
              {rename.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{search.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {search.scope === 'org'
                ? 'This is a shared search — deleting it removes it for everyone.'
                : 'This removes the saved search. The issues it matched are unaffected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault() // keep the dialog open until the commit resolves
                submitDelete()
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarMenuItem>
  )
}
